import React, { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PasswordChangePrompt from "@/components/PasswordChangePrompt";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import VersionUpdateNotifier from "@/components/VersionUpdateNotifier";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Each route is code-split with React.lazy so a public employee opening
// /form/{token} no longer downloads the entire admin bundle (AdminDashboard +
// MailBlastComposer + SurveyManager + …). LoginPage stays eager — it's tiny and
// is the immediate landing for unauthenticated users, so a chunk fetch there
// would only add a flash.
import LoginPage from "@/pages/LoginPage";
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const EmployeeForm = lazy(() => import("@/pages/EmployeeForm"));
const TrackingDashboard = lazy(() => import("@/pages/TrackingDashboard"));
const NewIntakePage = lazy(() => import("@/pages/NewIntakePage"));
const StudentIntakePage = lazy(() => import("@/pages/StudentIntakePage"));
const DiscountEntryPage = lazy(() => import("@/pages/DiscountEntryPage"));
const ClusterViewPage = lazy(() => import("@/pages/ClusterViewPage"));
const SignPage = lazy(() => import("@/pages/SignPage"));
const ChangeDetailsPage = lazy(() => import("@/pages/ChangeDetailsPage"));
const PrivacyTrainingPage = lazy(() => import("@/pages/PrivacyTrainingPage"));
const FormPreviewPage = lazy(() => import("@/pages/FormPreviewPage"));
const PublicFormPage = lazy(() => import("@/pages/PublicFormPage"));
const SurveyPublicPage = lazy(() => import("@/pages/SurveyPublicPage"));
const SurveyGeneralEntry = lazy(() => import("@/pages/SurveyGeneralEntry"));

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin w-8 h-8 border-4 border-secondary border-t-primary rounded-full" />
    </div>
  );
}

// Roles that reach the admin shell. The educational (branch-scoped) roles
// are included so they can use the dashboard; the sidebar then shows them
// only the "students" tab. Branch-level data scoping is enforced server-side.
const ADMIN_SHELL_ROLES = [
  "admin", "secretary", "educational_coordinator", "branch_manager", "teacher",
  // "צופה": sees only the surveys and form fill previews whose viewer lists
  // hold her email (formerly survey_viewer, surveys only).
  "viewer",
];

export function canAccessAdmin(user) {
  return ADMIN_SHELL_ROLES.includes(user?.role);
}

function canAccessTracking(user) {
  return ["admin", "secretary", "user"].includes(user?.role);
}

// Where to go back to after login: only a same-site path, never "//host".
export function safeReturnPath(from) {
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") && !from.startsWith("/login")
    ? from
    : "";
}

function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <FullScreenSpinner />;
  }
  // Not logged in: remember the page so a link sent by mail (e.g. a form's
  // fill preview) opens right after login instead of the dashboard.
  if (!user || user === false) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}<PasswordChangePrompt /></>;
}

function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const from = safeReturnPath(location.state?.from);
  if (user && from) return <Navigate to={from} replace />;
  if (canAccessAdmin(user)) return <Navigate to="/admin" replace />;
  if (user?.role === "user") return <Navigate to="/tracking" replace />;
  return <LoginPage />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenSpinner />;
  }

  return (
    <Suspense fallback={<FullScreenSpinner />}>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={ADMIN_SHELL_ROLES}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/forms/:formType/preview" element={<ProtectedRoute allowedRoles={ADMIN_SHELL_ROLES}><FormPreviewPage /></ProtectedRoute>} />
      <Route path="/admin/employees/:employeeId/change-details" element={<ProtectedRoute allowedRoles={["admin", "secretary"]}><ChangeDetailsPage /></ProtectedRoute>} />
      <Route path="/tracking" element={<ProtectedRoute allowedRoles={["admin", "secretary", "user"]}><TrackingDashboard /></ProtectedRoute>} />
      <Route path="/new" element={<NewIntakePage />} />
      <Route path="/intake/student" element={<StudentIntakePage />} />
      <Route path="/discount" element={<DiscountEntryPage />} />
      <Route path="/cluster/:token" element={<ClusterViewPage />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/p/:publicCode" element={<PublicFormPage />} />
      <Route path="/s/g/:code" element={<SurveyGeneralEntry />} />
      <Route path="/s/:token" element={<SurveyPublicPage />} />
      <Route path="/admin/calibration" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/fields" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/users" element={<Navigate to="/admin" replace />} />
      <Route path="/form/:token" element={<EmployeeForm />} />
      <Route path="/change-details" element={<ChangeDetailsPage />} />
      <Route path="/change-details/:token" element={<ChangeDetailsPage />} />
      <Route path="/privacy-training/:token" element={<PrivacyTrainingPage />} />
      <Route path="/" element={<Navigate to={canAccessAdmin(user) ? "/admin" : (canAccessTracking(user) ? "/tracking" : "/login")} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div dir="rtl" lang="he">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <ImpersonationBanner />
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
        <VersionUpdateNotifier />
      </ErrorBoundary>
    </div>
  );
}

export default App;
